import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../_shared/components/states/error-state/error-state.component';
import { PaginationComponent } from '../../../../../_shared/components/pagination/pagination.component';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import {
  createApproveMaintenanceRequestConfirmation,
  createBulkApproveMaintenanceRequestsConfirmation,
  createRejectMaintenanceRequestConfirmation,
  createResetMaintenanceRequestConfirmation
} from '../../constants/maintenance-confirmation.constants';
import { MaintenanceRequestDetailsResponse } from '../../models/maintenance-overview-response.model';
import { MaintenanceRequestAction } from '../../models/maintenance-overview.model';
import { MaintenanceStatus } from '../../../../_shared/enums/maintenance-status.enum';
import { MaintenanceModel } from '../../../../_shared/enums/maintenance-model.enum';
import { ServiceTypes } from '../../../../_shared/enums/service-type.enum';
import { AuthService } from '../../../../../_shared/services';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { MaintenanceSource } from '../../../../_shared/enums/maintenance-source.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance-requests-table',
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    EmptyStateComponent,
    ErrorStateComponent,
    PaginationComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './maintenance-requests-table.component.html',
  styleUrl: './maintenance-requests-table.component.scss'
})
export class MaintenanceRequestsTableComponent implements OnChanges {
  @Input() maintenanceRequests: MaintenanceRequestDetailsResponse[] = [];
  @Input() isLoading: boolean = false;
  @Input() hasError: boolean = false;
  @Input() searchTerm: string = '';
  @Input() currentPage: number = 1;
  @Input() pageSize: number = 10;
  @Input() totalCount: number = 0;
  @Input() totalPages: number = 1;
  @Input() selectionResetToken: number = 0;

  @Output() searchTermUpdated = new EventEmitter<string>();
  @Output() exportClicked = new EventEmitter<void>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() approveClicked = new EventEmitter<MaintenanceRequestAction>();
  @Output() rejectClicked = new EventEmitter<MaintenanceRequestAction>();
  @Output() dataRefreshNeeded = new EventEmitter<void>();
  @Output() bulkApproveClicked = new EventEmitter<MaintenanceRequestDetailsResponse[]>();

  maintenanceStatus = MaintenanceStatus;
  selectedRows: Set<number> = new Set();
  currencySymbol: string = '';
  currencyCode: string = '';
  private transloco = inject(TranslocoService);

  serviceTypeLabels: Record<ServiceTypes, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('maintenanceOverview.serviceTypeShort.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('maintenanceOverview.serviceTypeShort.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('maintenanceOverview.serviceTypeShort.major')
  };

  private allLoadedRequests: MaintenanceRequestDetailsResponse[] = [];
  private visitedPages: Set<number> = new Set();

  private authService = inject(AuthService);
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr = inject(ToastrService);

  constructor() {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectionResetToken'] && !changes['selectionResetToken'].firstChange) {
      this.selectedRows.clear();
      this.allLoadedRequests = [];
      this.visitedPages.clear();
    }

    if (changes['maintenanceRequests']) {
      const merged = [...this.allLoadedRequests, ...this.maintenanceRequests];
      const byId = new Map<number, MaintenanceRequestDetailsResponse>();
      merged.forEach(row => byId.set(row.id, row));
      this.allLoadedRequests = Array.from(byId.values());
      this.visitedPages.add(this.currentPage);
    }
  }

  get selectableRows(): MaintenanceRequestDetailsResponse[] {
    return this.maintenanceRequests.filter(row => row.maintenanceStatus !== MaintenanceStatus.Rejected);
  }

  private get loadedSelectableCount(): number {
    return this.allLoadedRequests.filter(r => r.maintenanceStatus !== MaintenanceStatus.Rejected).length;
  }

  private get hasLoadedAllPages(): boolean {
    return this.totalPages > 0 && this.visitedPages.size >= this.totalPages;
  }

  get isAllSelected(): boolean {
    if (this.selectedRows.size === 0) return false;
    if (!this.hasLoadedAllPages) return false;
    const globalSelectable = this.loadedSelectableCount;
    return globalSelectable > 0 && this.selectedRows.size === globalSelectable;
  }

  get isIndeterminate(): boolean {
    return this.selectedRows.size > 0 && !this.isAllSelected;
  }

  isRowSelected(id: number): boolean {
    return this.selectedRows.has(id);
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  formatCurrency(value: number): string {
    if (!value) return '-';
    return `${this.currencySymbol}${value.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  }

  formatRangeLeft(rangeLeft: number): string {
    if (!rangeLeft) return '-';
    return `${rangeLeft.toLocaleString('default', { maximumFractionDigits: 2 })} km`;
  }

  formatMaintenanceType(isAutoTrigger: boolean, type: ServiceTypes): string {
    return isAutoTrigger
      ? this.serviceTypeLabels[type]
      : this.transloco.translate('maintenanceOverview.reactiveMaintenanceType', { serviceType: this.serviceTypeLabels[type] });
  }

  getMaintenanceModel(model: MaintenanceModel): string {
    return model === MaintenanceModel.InHouse
      ? this.transloco.translate('maintenanceOverview.model.inHouse')
      : this.transloco.translate('maintenanceOverview.model.contract');
  }

  getMaintenanceSource(source: MaintenanceSource): string {
    switch (source) {
      case MaintenanceSource.OEM:
        return this.transloco.translate('maintenanceOverview.source.oem');
      case MaintenanceSource.Custom:
        return this.transloco.translate('maintenanceOverview.source.custom');
      case MaintenanceSource.System:
        return this.transloco.translate('maintenanceOverview.source.system');
      case MaintenanceSource.DriverApp:
        return this.transloco.translate('maintenanceOverview.source.driverApp');
      case MaintenanceSource.ComplianceApp:
        return this.transloco.translate('maintenanceOverview.source.complianceApp');
      case MaintenanceSource.CanBus:
        return this.transloco.translate('maintenanceOverview.source.canBus');
      default:
        return '';
    }
  }

  onSelectAllClick(event: MouseEvent): void {
    event.preventDefault();

    const selectable = this.selectableRows;
    if (selectable.length === 0) return;

    const isCurrentPageFullySelected = selectable.every(row => this.selectedRows.has(row.id));
    if (isCurrentPageFullySelected) {
      selectable.forEach(row => this.selectedRows.delete(row.id));
    } else {
      selectable.forEach(row => this.selectedRows.add(row.id));
    }
  }

  onRowSelectChange(id: number, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.selectedRows.add(id);
    } else {
      this.selectedRows.delete(id);
    }
  }

  get showingCount(): number {
    return Math.min((this.currentPage - 1) * this.pageSize + this.pageSize, this.totalCount);
  }

  onSearchTermUpdate(searchTerm: string): void {
    this.searchTermUpdated.emit(searchTerm);
  }

  onExportClick(): void {
    this.exportClicked.emit();
  }

  onPageChange(page: number): void {
    if (page !== this.currentPage && page >= 1 && page <= this.totalPages) {
      this.pageChanged.emit(page);
    }
  }

  onApproveClick(item: MaintenanceRequestDetailsResponse): void {
    let isSuccessful = false;

    const options = createApproveMaintenanceRequestConfirmation(this.transloco, async () => {
      this.selectedRows.delete(item.id);
      isSuccessful = await this.emitRequestAction(this.approveClicked, item);
    });

    this.confirmationModalService.showConfirmationModal(options)
      .then(() => this.showActionResult(
        isSuccessful,
        this.transloco.translate('maintenanceOverview.toasts.requestApproved'),
        this.transloco.translate('maintenanceOverview.toasts.requestApproveFailed')
      ))
      .catch(() => { /* dismissed — no action taken, no toast */ });
  }

  onRejectClick(item: MaintenanceRequestDetailsResponse): void {
    let isSuccessful = false;

    const options = createRejectMaintenanceRequestConfirmation(this.transloco, async () => {
      this.selectedRows.delete(item.id);
      isSuccessful = await this.emitRequestAction(this.rejectClicked, item);
    });

    this.confirmationModalService.showConfirmationModal(options)
      .then(() => this.showActionResult(
        isSuccessful,
        this.transloco.translate('maintenanceOverview.toasts.requestRejected'),
        this.transloco.translate('maintenanceOverview.toasts.requestRejectFailed')
      ))
      .catch(() => { /* dismissed — no action taken, no toast */ });
  }

  onRejectedClick(item: MaintenanceRequestDetailsResponse): void {
    let isSuccessful = false;

    const options = createResetMaintenanceRequestConfirmation(this.transloco, async () => {
      isSuccessful = await this.emitRequestAction(this.rejectClicked, item);
    });

    this.confirmationModalService.showConfirmationModal(options)
      .then(() => this.showActionResult(
        isSuccessful,
        this.transloco.translate('maintenanceOverview.toasts.requestReset'),
        this.transloco.translate('maintenanceOverview.toasts.requestResetFailed')
      ))
      .catch(() => { /* dismissed — no action taken, no toast */ });
  }

  private emitRequestAction(
    emitter: EventEmitter<MaintenanceRequestAction>,
    item: MaintenanceRequestDetailsResponse
  ): Promise<boolean> {
    return new Promise<boolean>(resolve => emitter.emit({ item, onComplete: resolve }));
  }

  private showActionResult(isSuccessful: boolean, successMessage: string, errorMessage: string): void {
    if (isSuccessful) {
      this.toastr.success(successMessage);
      return;
    }

    this.toastr.error(errorMessage);
  }

  onEditClick(item: MaintenanceRequestDetailsResponse): void {
    this.dataRefreshNeeded.emit();
  }

  onBulkApproveClick(): void {
    const selectedItems = this.allLoadedRequests.filter(item => this.selectedRows.has(item.id));
    const count = selectedItems.length;
    if (count === 0) return;

    const options = createBulkApproveMaintenanceRequestsConfirmation(this.transloco, count);

    this.confirmationModalService.showConfirmationModal(options).then((confirmed) => {
      if (confirmed) {
        this.bulkApproveClicked.emit(selectedItems);
        this.selectedRows.clear();
        this.allLoadedRequests = [];
      }
    });
  }
}